// src/components/PatronManagementView/AddPhysicalCredentialModal.js
import React, { useState } from "react";
import config from "../../config";
import ConfirmActionModal from "../../ConfirmActionModal";
import "./AddPhysicalCredentialModal.css";

const FORMAT_LIMITS = {
  "26A": { facilityMax: 255, badgeMax: 65535 },
  "32X": { facilityMax: null, badgeMax: 1073741823 },
  "35X": { facilityMax: 4095, badgeMax: 1048575 },
  "36X": { facilityMax: 4095, badgeMax: 2097151 },
  "37X": { facilityMax: 65535, badgeMax: 524287 },
  "40X": { facilityMax: 1023, badgeMax: 268435455 },
  "48X": { facilityMax: 1048575, badgeMax: 1048575 },
  "56W": { facilityMax: 16777215, badgeMax: 4294967295 },
};

const BIT_FORMATS = Object.keys(FORMAT_LIMITS);

const AddPhysicalCredentialModal = ({ patronId, orgId, onClose, onCredentialCreated, getAccessToken }) => {
  const [bitFormat, setBitFormat] = useState("");
  const [issueCode, setIssueCode] = useState(1);
  const [facilityCode, setFacilityCode] = useState(55);
  const [badgeId, setBadgeId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const limits = bitFormat ? FORMAT_LIMITS[bitFormat] : null;

  const rejectNonDigits = (e) => {
    if (!/[\d]/.test(e.key) && !["Backspace", "Delete", "Tab", "ArrowLeft", "ArrowRight"].includes(e.key)) {
      e.preventDefault();
    }
  };

  const validate = () => {
    if (!bitFormat) return "Please select a bit format.";
    if (!limits) return "Unknown bit format.";

    const badgeIdNum = Number(badgeId);
    if (badgeId === "" || !Number.isInteger(badgeIdNum) || badgeIdNum < 0 || badgeIdNum > limits.badgeMax) {
      return `Badge ID must be an integer between 0 and ${limits.badgeMax.toLocaleString()}.`;
    }

    if (bitFormat !== "32X") {
      const fcNum = Number(facilityCode);
      if (!Number.isInteger(fcNum) || fcNum < 0 || fcNum > limits.facilityMax) {
        return `Facility Code must be an integer between 0 and ${limits.facilityMax.toLocaleString()}.`;
      }
    }

    if (bitFormat === "48X") {
      const icNum = Number(issueCode);
      if (!Number.isInteger(icNum) || icNum < 1 || icNum > 63) {
        return "Issue Code must be an integer between 1 and 63.";
      }
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    try {
      const accessToken = getAccessToken ? await getAccessToken() : null;

      const body = {
        bitFormat,
        badgeId: Number(badgeId),
        patronId,
        orgId,
      };
      if (bitFormat !== "32X") body.facilityCode = Number(facilityCode);
      if (bitFormat === "48X") body.issueCode = Number(issueCode);

      const response = await fetch(`${config.servicesApiEndpoint}/physical-credentials`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": config.apiKey,
          ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
        },
        body: JSON.stringify(body),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setError(data.error || `Error ${response.status}: Failed to create credential.`);
        return;
      }

      const deviceIds = data.deviceIds ? [...data.deviceIds] : [];
      if (deviceIds.length === 0) {
        setSuccessMessage("Physical credential created. No devices found in this organization — the credential will be provisioned automatically when devices are connected.");
      } else {
        setSuccessMessage(`Physical credential created and queued for provisioning to ${deviceIds.length} device${deviceIds.length !== 1 ? "s" : ""}.`);
      }
      setShowSuccessModal(true);
    } catch (err) {
      setError(err.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <ConfirmActionModal
      isOpen={showSuccessModal}
      onClose={() => { setShowSuccessModal(false); onClose(); onCredentialCreated(); }}
      onConfirm={() => { setShowSuccessModal(false); onClose(); onCredentialCreated(); }}
      title="Credential Created"
      message={successMessage || ""}
      confirmButtonText="OK"
      cancelButtonText={null}
      isDestructive={false}
    />
    <div className="add-physical-credential-backdrop">
      <div className="add-physical-credential-modal">
        <div className="add-physical-credential-header">
          <h2>Add Physical Credential</h2>
          <button className="add-physical-credential-close-btn" onClick={onClose} disabled={loading}>
            ✕
          </button>
        </div>

        <form className="add-physical-credential-form" onSubmit={handleSubmit}>
          <div className="add-physical-credential-field">
            <label htmlFor="bitFormat">Bit Format</label>
            <select
              id="bitFormat"
              value={bitFormat}
              onChange={(e) => { setBitFormat(e.target.value); setError(null); }}
              disabled={loading}
              required
            >
              <option value="">— Select format —</option>
              {BIT_FORMATS.map((fmt) => (
                <option key={fmt} value={fmt}>{fmt}</option>
              ))}
            </select>
          </div>

          {bitFormat === "48X" && (
            <div className="add-physical-credential-field">
              <label htmlFor="issueCode">Issue Code (1–63)</label>
              <input
                id="issueCode"
                type="number"
                min={1}
                max={63}
                value={issueCode}
                onChange={(e) => setIssueCode(e.target.value)}
                onKeyDown={rejectNonDigits}
                disabled={loading}
              />
            </div>
          )}

          {bitFormat && bitFormat !== "32X" && (
            <div className="add-physical-credential-field">
              <label htmlFor="facilityCode">
                Facility Code {limits ? `(0–${limits.facilityMax.toLocaleString()})` : ""}
              </label>
              <input
                id="facilityCode"
                type="number"
                min={0}
                max={limits?.facilityMax}
                value={facilityCode}
                onChange={(e) => setFacilityCode(e.target.value)}
                onKeyDown={rejectNonDigits}
                disabled={loading}
              />
            </div>
          )}

          {bitFormat && (
            <div className="add-physical-credential-field">
              <label htmlFor="badgeId">
                Badge ID {limits ? `(0–${limits.badgeMax.toLocaleString()})` : ""}
              </label>
              <input
                id="badgeId"
                type="text"
                inputMode="numeric"
                value={badgeId}
                onChange={(e) => setBadgeId(e.target.value)}
                onKeyDown={rejectNonDigits}
                disabled={loading}
                required
              />
            </div>
          )}

          {error && <div className="add-physical-credential-error">{error}</div>}
          {successMessage && <div className="add-physical-credential-success">{successMessage}</div>}

          <div className="add-physical-credential-actions">
            <button
              type="button"
              className="add-physical-credential-cancel-btn"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="add-physical-credential-submit-btn"
              disabled={loading || !bitFormat || badgeId === ""}
            >
              {loading ? "Creating…" : "Create Credential"}
            </button>
          </div>
        </form>
      </div>
    </div>
    </>
  );
};

export default AddPhysicalCredentialModal;
